<template>
  <!--
    The header of the marketplace door.

    It is the whole chrome of the front: the brand name as the one heading and
    the language switcher, and nothing else. No navigation links at all -- the
    pages that used to live here are gone from the routing, so a link to them
    would be a link to a 404.

    The colour is the platform's own palette (the violet of the footer and the
    navbar), so the door looks like the platform it belongs to instead of a
    stripped-down page.
  -->
  <header class="marketplace-header" data-test="marketplace-header">
    <h1 class="marketplace-header__title">{{ siteName }}</h1>

    <div class="marketplace-header__lang" role="group" :aria-label="$t('website.menu.language')">
      <button
        type="button"
        class="marketplace-header__lang-btn"
        :class="{ 'is-active': currentLocale === 'es' }"
        data-test="marketplace-lang-es"
        @click="changeLanguage('es')"
      >
        ES
      </button>
      <button
        type="button"
        class="marketplace-header__lang-btn"
        :class="{ 'is-active': currentLocale === 'en' }"
        data-test="marketplace-lang-en"
        @click="changeLanguage('en')"
      >
        EN
      </button>
    </div>
  </header>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { loadBranding } from '@/services/branding.service';

const { locale } = useI18n();

const currentLocale = computed(() => locale.value);

// The brand name comes from the per-host bootstrap payload, the same source
// the navbar reads: it is the name of the host being served, not a constant
// baked into the bundle.
const siteName = computed(() => loadBranding().siteName);

const changeLanguage = (lang: string) => {
  locale.value = lang;
  localStorage.setItem('user-locale', lang);
};
</script>

<style scoped>
.marketplace-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: clamp(1rem, 3vw, 1.75rem) clamp(1rem, 5vw, 2.5rem);
}

/*
 * The wordmark. The gradient is clipped to the text, so the word is painted
 * with the platform's palette instead of a flat colour. `color: transparent`
 * is what makes the clip visible; the text itself is still there for anything
 * that reads it without a stylesheet.
 */
.marketplace-header__title {
  margin: 0;
  font-size: clamp(1.9rem, 5.5vw, 2.8rem);
  font-weight: 950;
  line-height: 1.1;
  letter-spacing: -0.04em;
  background: linear-gradient(135deg, #ffffff 0%, #e9d5ff 55%, #c084fc 100%);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}

.marketplace-header__lang {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.3rem;
  border-radius: 999px;
  background: rgb(255 255 255 / 10%);
  border: 1px solid rgb(255 255 255 / 18%);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
}

.marketplace-header__lang-btn {
  border: 0;
  background: transparent;
  color: rgb(255 255 255 / 72%);
  font-size: 0.8rem;
  font-weight: 800;
  letter-spacing: 0.04em;
  padding: 0.35rem 0.7rem;
  border-radius: 999px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.marketplace-header__lang-btn:hover {
  color: #ffffff;
}

.marketplace-header__lang-btn.is-active {
  background: #ffffff;
  color: #2d0066;
}
</style>
